/* @flow */

import clsx from 'clsx';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { KeyValuePair } from '../../helpers/jsHelpers/types';

import { AppFooterContent, AppFooterLogo } from '../../helpers/applicationCustomization/ui';
import { fireAndForget } from '../../helpers/jsHelpers/promise';
import Localizer from '../../helpers/localization';
import { Messenger, MessengerEvents } from '../../helpers/messenger';
import { openFooterPage } from '../../helpers/ui/browser';
import { getSettingValueByName } from '../../redux/netgemApi/actions/helpers/settings';
import { getImageUrl } from '../../redux/netgemApi/actions/v8/metadataImage';
import { PictoAndroid, PictoApple, PictoArrowUp, PictoFacebook, PictoInstagram, PictoLinkedIn, PictoTwitter, PictoYouTube } from '../pictos/Element';
import './Footer.css';
import Version from './Version';

const FOOTER_LOGO_HEIGHT = 58;
const FOOTER_LOGO_WIDTH = 170;

type FooterPropType = {
  +isVisible: boolean,
};

const Footer = ({ isVisible }: FooterPropType): React.Node => {
  const dispatch = useDispatch();

  const configuration = useSelector<KeyValuePair<string>>((state) => state.appConfiguration.configuration);
  const isInTheaterMode = useSelector<boolean>((state) => state.ui.isInTheaterMode);

  // Check for distributor logo which, if any, overrides regular logo
  const distributorLogoId = useSelector<string | null>((state) => getSettingValueByName('app.toolbar', 'logoid', state.appConfiguration));

  const [distributorLogo, setDistributorLogo] = useState<string | null>(null);

  useEffect(() => {
    const loadLogo = async (assetId: string) => {
      const logoUrl = await dispatch(getImageUrl({ assetId, height: FOOTER_LOGO_HEIGHT, width: FOOTER_LOGO_WIDTH }));
      setDistributorLogo(logoUrl);
    };

    if (distributorLogoId === null) {
      return;
    }

    fireAndForget(loadLogo(distributorLogoId), 'Header.loadLogo');
  }, [distributorLogoId]);

  const handleTopButtonOnClick = useCallback(() => {
    Messenger.emit(MessengerEvents.MOVE_TO_TOP);

    // 1. Try to focus on the first menu item (first avenue)
    const firstNavItem = document.querySelector('nav button:not(.skipLink)');

    if (firstNavItem instanceof HTMLElement) {
      firstNavItem.focus();
      return;
    }

    // 2. Fallback to header or main landmark
    const fallbackElement = document.querySelector('header') || document.querySelector('main');
    if (fallbackElement instanceof HTMLElement) {
      if (fallbackElement.getAttribute('tabindex') === null) {
        fallbackElement.setAttribute('tabindex', '-1');
      }
      fallbackElement.focus();
    }
  }, []);

  const handleKeyboardShortcutsOnClick = useCallback(() => {
    Messenger.emit(MessengerEvents.SHOW_KEYBOARD_SHORTCUTS);
  }, []);

  const handleLegalNoticeOnClick = useCallback(() => {
    Messenger.emit(MessengerEvents.SHOW_LEGAL_NOTICE);
  }, []);

  return (
    <footer className={clsx('footer', (isInTheaterMode && 'slideDown') || (isVisible && 'slideUp'))} inert={isInTheaterMode}>
      <div className='footerCommon'>
        <PictoArrowUp aria-label={Localizer.localize('accessibility.navigation.go_to_top')} className='topButton' onClick={handleTopButtonOnClick} />
      </div>
      <React.Suspense fallback={<div>Error loading footer content</div>}>
        <AppFooterContent
          configuration={configuration}
          images={{ footerLogo: distributorLogo ?? AppFooterLogo, PictoAndroid, PictoApple, PictoFacebook, PictoInstagram, PictoLinkedIn, PictoTwitter, PictoYouTube }}
          localize={Localizer.localize}
          openFooterPage={openFooterPage}
          showKeyboardShortcuts={handleKeyboardShortcutsOnClick}
          showLegalNotice={handleLegalNoticeOnClick}
        />
      </React.Suspense>
      <Version />
    </footer>
  );
};

export default Footer;
